/**
 * Display labels for a string's inline-markup and message-format enums, shared because the editor
 * modals, the view modal and both version-diff builders must render a format with the same words.
 * The values are resource IDS — resolve with {@link inlineMarkupLabel} / {@link messageFormatLabel}.
 */
import { InlineMarkupType, MessageFormatType } from 'src/generated/graphql';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

// Keyed by the enum rather than `string` so adding a format to the schema fails
// the build here instead of silently rendering the raw enum value.
export const INLINE_MARKUP_LABEL_IDS: Record<InlineMarkupType, string> = {
  [InlineMarkupType.PlainText]: 'format-inline-plain-text',
  [InlineMarkupType.Html]: 'format-inline-html',
  [InlineMarkupType.Markdown]: 'format-inline-markdown',
};

export const MESSAGE_FORMAT_LABEL_IDS: Record<MessageFormatType, string> = {
  [MessageFormatType.None]: 'format-message-none',
  [MessageFormatType.HashGet]: 'format-message-hash-get',
  [MessageFormatType.AmazonMessageFormat]: 'format-message-amazon',
  [MessageFormatType.Mustache]: 'format-message-mustache',
};

/** The leading dropdown option meaning "format unknown, leave it alone", which omits the field
 * from the submission rather than sending a value. */
export const UNKNOWN_FORMAT_LABEL_ID = 'format-unknown-keep';

/** Takes a `string` because the value arrives from the API and may be outside the generated enum;
 * such a value has no resource and is returned verbatim rather than rendering nothing. */
export function inlineMarkupLabel(t: TranslateFn, value: string): string {
  const id: string | undefined =
    INLINE_MARKUP_LABEL_IDS[value as InlineMarkupType];
  return id ? t(id) : value;
}

/** See {@link inlineMarkupLabel}. */
export function messageFormatLabel(t: TranslateFn, value: string): string {
  const id: string | undefined =
    MESSAGE_FORMAT_LABEL_IDS[value as MessageFormatType];
  return id ? t(id) : value;
}
